import { useState, useEffect } from 'react';
import {
    Upload,
    Download,
    RefreshCw,
    AlertTriangle,
    CheckCircle,
    Package,
    Rocket,
    Info,
    Clock,
    Shield,
    Zap,
    X
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../config/api';
import Portal from '../components/Portal';

export default function Updates() {
    const [currentVersion, setCurrentVersion] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [installing, setInstalling] = useState(false);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [updateStatus, setUpdateStatus] = useState(null);
    const [polling, setPolling] = useState(false);

    useEffect(() => {
        fetchCurrentVersion();
    }, []);

    const [restarting, setRestarting] = useState(false);

    const handleFinalRestart = async () => {
        setRestarting(true);
        const restartToast = toast.loading('Sending restart command...');
        try {
            const response = await api.post('/admin/updates/restart');
            if (response.data.success) {
                toast.success('Restart command sent! Reconnecting in 10 seconds...', { id: restartToast });

                // Wait 10 seconds and then force reload to reconnect
                setTimeout(() => {
                    window.location.reload();
                }, 10000);
            } else {
                toast.error(response.data.error || 'Restart failed', { id: restartToast });
                setRestarting(false);
            }
        } catch (error) {
            // Error is expected if the server restarts immediately
            toast.success('System is rebooting... Wait a moment.', { id: restartToast });
            setTimeout(() => {
                window.location.reload();
            }, 10000);
        }
    };

    const fetchCurrentVersion = async () => {
        try {
            const response = await api.get('/admin/updates/info');

            if (response.data.success) {
                setCurrentVersion(response.data.currentVersion);
            }
        } catch (error) {
            console.error('Failed to fetch version:', error);
            toast.error('Failed to load version information');
        }
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];

        if (!file) return;

        if (!file.name.endsWith('.zip')) {
            setError('Please select a .zip file');
            toast.error('Please select a .zip file');
            return;
        }

        setSelectedFile(file);
        setError('');
        toast.success(`Selected: ${file.name}`);
    };

    const [uploadProgress, setUploadProgress] = useState(0);

    const handleUpload = async () => {
        if (!selectedFile) {
            setError('Please select a file first');
            toast.error('Please select a file first');
            return;
        }

        setUploading(true);
        setUploadProgress(0);
        setError('');
        setMessage('');

        const uploadToast = toast.loading('Uploading update package (0%)...');

        try {
            const formData = new FormData();
            formData.append('updateFile', selectedFile);

            const response = await api.post('/admin/updates/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                timeout: 600000, // 10 minutes for large update files
                onUploadProgress: (progressEvent) => {
                    const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setUploadProgress(progress);
                    toast.loading(`Uploading update package (${progress}%)...`, { id: uploadToast });
                }
            });

            if (response.data.success) {
                setMessage('Update package uploaded successfully!');
                setUploadedFile(response.data.filename);
                setSelectedFile(null);
                toast.success('Update package uploaded successfully!', { id: uploadToast });
            } else {
                setError(response.data.error || 'Upload failed');
                toast.error(response.data.error || 'Upload failed', { id: uploadToast });
            }
        } catch (error) {
            const errorMsg = error.response?.data?.error || error.message || 'Upload failed';
            setError('Upload failed: ' + errorMsg);
            toast.error('Upload failed: ' + errorMsg, { id: uploadToast });
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }
    };

    const handleInstallClick = () => {
        if (!uploadedFile) {
            setError('No update package uploaded');
            toast.error('No update package uploaded');
            return;
        }
        setShowConfirmModal(true);
    };

    const handleConfirmInstall = async () => {
        setShowConfirmModal(false);
        setInstalling(true);
        setError('');
        setMessage('');

        try {
            const response = await api.post('/admin/updates/install', {
                filename: uploadedFile
            });

            if (response.data.success) {
                setMessage('Update installation started!');
                toast.success('Update installation started!');
                // Wait 2 seconds for the worker to actually start writing to the status file
                setTimeout(() => startStatusPolling(), 2000);
            } else {
                setError(response.data.error || 'Installation failed');
                toast.error(response.data.error || 'Installation failed');
                setInstalling(false);
            }
        } catch (error) {
            const errorMsg = error.response?.data?.error || error.message || 'Installation failed';
            setError('Installation failed: ' + errorMsg);
            toast.error('Installation failed: ' + errorMsg);
            setInstalling(false);
        }
    };

    const startStatusPolling = () => {
        setPolling(true);
        let errorCount = 0;
        const interval = setInterval(async () => {
            try {
                const response = await api.get('/admin/updates/status');
                if (response.data.success && response.data.status) {
                    setUpdateStatus(response.data.status);
                    errorCount = 0; // Reset error count on successful poll

                    if (response.data.status.isDone) {
                        clearInterval(interval);
                        setPolling(false);

                        if (response.data.status.error) {
                            setError('Update failed: ' + response.data.status.error);
                            setInstalling(false);
                        } else if (response.data.status.needsRestart) {
                            // NEW: Manual restart mode
                            setMessage('Update ready! System restart required.');
                            setUpdateStatus(prev => ({ ...prev, progress: 100, stage: 'Ready', message: 'Files replaced. Please restart the system to apply changes.' }));
                        } else {
                            // Legacy auto-restart mode
                            setMessage('Update completed successfully! Reloading...');
                            window.location.reload();
                        }
                    }
                }
            } catch (err) {
                // If progress is already high (>= 90%) and connection fails,
                // it's highly likely the server is currently restarting.
                setUpdateStatus(prev => {
                    if (prev && prev.progress >= 90 && !prev.isDone) {
                        // Switch to a predictive "Restarting" state
                        return {
                            ...prev,
                            progress: 100,
                            stage: 'Restarting',
                            message: 'Server rebooting... reconnecting shortly.'
                        };
                    }
                    return prev;
                });

                console.log('Poll connectivity interrupted (normal during update)...');
                errorCount++;

                // Only give up after a long time (e.g. 10 minutes)
                if (errorCount > 400) {
                    clearInterval(interval);
                    setPolling(false);
                    setError('Update connection lost for too long. Please refresh manually.');
                }
            }
        }, 1500);
    };

    return (
        <div className="max-w-4xl mx-auto space-y-8 pb-12 relative animate-fade-in">
            {/* Premium Header */}
            <div className="flex items-center justify-between bg-gradient-to-r from-brand-50 to-primary-100 dark:from-brand-900/20 dark:to-primary-900/10 rounded-3xl shadow-sm p-8 mb-4 border border-brand-100 dark:border-brand-900/30">
                <div className="flex items-center gap-6">
                    <div className="h-16 w-16 bg-white dark:bg-slate-800 rounded-2xl flex items-center justify-center shadow-lg transform -rotate-3 hover:rotate-0 transition-transform duration-300">
                        <RefreshCw className="h-8 w-8 text-brand-600 dark:text-brand-400" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">System Updates</h1>
                        <p className="mt-1 text-slate-600 dark:text-slate-400 font-medium max-w-md">
                            Keep your Smart Email Verifier up to date with the latest features and security improvements
                        </p>
                    </div>
                </div>
            </div>

            {/* Step 1: Current Version Card */}
            <div className="card">
                <div className="card-header border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="h-12 w-12 bg-brand-100 dark:bg-brand-900/30 rounded-2xl flex items-center justify-center text-brand-600 dark:text-brand-400 font-black text-lg">
                            1
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-slate-900 dark:text-white">Current Version</h2>
                            <p className="text-sm text-slate-500 dark:text-slate-400">Step 1: Check your current application version</p>
                        </div>
                    </div>
                    <div className="px-4 py-2 bg-brand-50 dark:bg-brand-900/20 rounded-xl border border-brand-100 dark:border-brand-800">
                        <span className="text-2xl font-black text-brand-600 dark:text-brand-400">
                            v{currentVersion || '...'}
                        </span>
                    </div>
                </div>
            </div>

            {/* Step 2: How to Update Instructions */}
            <div className="card">
                <div className="card-header border-b border-slate-100 dark:border-slate-800">
                    <div className="flex items-center gap-3">
                        <div className="h-12 w-12 bg-primary-100 dark:bg-primary-900/30 rounded-2xl flex items-center justify-center text-primary-600 dark:text-primary-400 font-black text-lg">
                            2
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-slate-900 dark:text-white">Update Instructions</h2>
                            <p className="text-sm text-slate-500 dark:text-slate-400">Step 2: Understand the update flow before proceeding</p>
                        </div>
                    </div>
                </div>
                <div className="card-body">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {[
                            { title: 'Prepare Package', desc: 'Download the latest version as a .zip file (must include source_code folder)', icon: Package },
                            { title: 'Shadow Build', desc: 'The system builds the update in background while site is live', icon: Zap },
                            { title: 'Safe Replacement', desc: 'Core files are swapped only after successful build', icon: Shield },
                            { title: 'Auto Restart', desc: 'All PM2 services restart automatically to apply changes', icon: RefreshCw }
                        ].map((step, i) => (
                            <div key={i} className="group p-5 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-200 dark:border-slate-700 hover:border-brand-300 transition-all duration-300">
                                <div className="h-10 w-10 bg-white dark:bg-slate-800 rounded-xl flex items-center justify-center mb-4 shadow-sm group-hover:shadow-md transition-all">
                                    <step.icon className="h-5 w-5 text-brand-500" />
                                </div>
                                <h4 className="font-bold text-slate-900 dark:text-white text-sm mb-2">{step.title}</h4>
                                <p className="text-[11px] leading-relaxed text-slate-600 dark:text-slate-400">{step.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Step 3: Action Area (Upload & Install) */}
            <div className="space-y-6">
                <div className="flex items-center gap-3 px-2">
                    <div className="h-12 w-12 bg-warning-100 dark:bg-warning-900/30 rounded-2xl flex items-center justify-center text-warning-600 dark:text-warning-500 font-black text-lg">
                        3
                    </div>
                    <h2 className="text-2xl font-black text-slate-900 dark:text-white">Execution Area</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Upload Component */}
                    <div className="card h-full">
                        <div className="card-body space-y-4">
                            {!uploadedFile ? (
                                <>
                                    <div className="group relative border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-2xl p-6 text-center hover:border-brand-400 transition-all cursor-pointer">
                                        <input type="file" id="updateFile" accept=".zip" onChange={handleFileSelect} disabled={uploading || installing} className="absolute inset-0 opacity-0 cursor-pointer" />
                                        <div className="space-y-4">
                                            <div className="h-12 w-12 bg-slate-100 dark:bg-slate-800 rounded-xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform">
                                                <Upload className="h-6 w-6 text-slate-400" />
                                            </div>
                                            <div className="space-y-1">
                                                <p className="font-bold text-sm text-slate-700 dark:text-white">{selectedFile ? selectedFile.name : 'Choose ZIP File'}</p>
                                                {uploading && (
                                                    <div className="w-full mt-2">
                                                        <div className="flex justify-between text-[10px] font-bold text-brand-500 mb-1">
                                                            <span>Uploading...</span>
                                                            <span>{uploadProgress}%</span>
                                                        </div>
                                                        <div className="w-full h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                                            <div
                                                                className="h-full bg-brand-500 transition-all duration-300"
                                                                style={{ width: `${uploadProgress}%` }}
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <button className="btn btn-primary w-full py-3" onClick={handleUpload} disabled={!selectedFile || uploading || installing}>
                                        {uploading ? <RefreshCw className="animate-spin h-5 w-5" /> : <Upload className="mr-2 h-5 w-5" />}
                                        {uploading ? 'Uploading...' : 'Upload Package'}
                                    </button>
                                    <div className="flex items-center gap-2 text-[10px] text-slate-500 justify-center">
                                        <Info className="h-3 w-3" />
                                        <span>ZIP must contain the <strong>source_code</strong> directory</span>
                                    </div>
                                </>
                            ) : (
                                <div className="p-4 bg-success-50 dark:bg-success-900/10 border border-success-200 dark:border-success-700 rounded-2xl flex flex-col items-center gap-3 text-center">
                                    <CheckCircle className="h-8 w-8 text-success-600" />
                                    <div>
                                        <p className="font-bold text-slate-900 dark:text-white">Ready to Install</p>
                                        <p className="text-xs text-slate-400 italic truncate w-48">{uploadedFile}</p>
                                    </div>
                                    <button className="text-xs font-bold text-brand-600" onClick={() => setUploadedFile(null)}>Change File</button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Install Component */}
                    <div className={`card h-full ${!uploadedFile ? 'opacity-40 cursor-not-allowed grayscale' : ''}`}>
                        <div className="card-body flex flex-col justify-center gap-4">
                            <button className="btn btn-danger w-full py-6 text-xl shadow-xl shadow-danger-500/20" onClick={handleInstallClick} disabled={!uploadedFile || installing}>
                                <Rocket className="mr-3 h-6 w-6" />
                                Start Installation
                            </button>
                            <div className="flex items-center justify-center gap-2 text-xs text-slate-500 font-bold">
                                <Clock className="h-4 w-4" />
                                Est. Downtime: &lt; 15 seconds
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Progress Tracking (Conditional) */}
            {installing && updateStatus && (
                <div className="card shadow-2xl border-brand-500 animate-slide-up bg-slate-900 text-white dark:border-brand-400">
                    <div className="p-8 space-y-6">
                        <div className="flex justify-between items-center">
                            <div className="space-y-1">
                                <h3 className="text-2xl font-black">{updateStatus.stage}</h3>
                                <p className="text-brand-300 font-medium italic">{updateStatus.message}</p>
                            </div>
                            <div className="text-4xl font-black text-brand-400">{updateStatus.progress}%</div>
                        </div>

                        <div className="w-full h-4 bg-white/10 rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-brand-500 to-primary-400 transition-all duration-700" style={{ width: `${updateStatus.progress}%` }} />
                        </div>

                        <div className="bg-warning-50 dark:bg-warning-900/20 p-4 rounded-xl border border-warning-200 dark:border-warning-700/50 flex items-start gap-4">
                            <AlertTriangle className="h-5 w-5 text-warning-600 dark:text-warning-500 flex-shrink-0 mt-0.5" />
                            <div className="space-y-3 flex-1">
                                <p className="text-xs font-bold text-warning-800 dark:text-warning-400 leading-tight">
                                    CAUTION: The update is being built and files are being replaced.
                                    {!updateStatus.needsRestart ? 'Do not close this tab.' : 'Updates are now ready to be applied.'}
                                </p>

                                {updateStatus.needsRestart && (
                                    <div className="pt-2">
                                        <button
                                            onClick={handleFinalRestart}
                                            disabled={restarting}
                                            className="w-full sm:w-auto flex items-center justify-center gap-3 px-6 py-3 bg-brand-600 text-white rounded-xl text-sm font-bold shadow-xl shadow-brand-500/30 hover:bg-brand-700 hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50"
                                        >
                                            {restarting ? (
                                                <RefreshCw className="h-4 w-4 animate-spin" />
                                            ) : (
                                                <RefreshCw className="h-4 w-4" />
                                            )}
                                            {restarting ? 'Rebooting System...' : 'Restart System Now to Apply Update'}
                                        </button>
                                        <p className="mt-2 text-[10px] text-slate-500 font-medium">
                                            This will restart all PM2 services and apply the changes.
                                            You will be automatically reconnected in 10 seconds.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Error/Message Overlays */}
            {error && (
                <div className="p-4 bg-danger-50 text-danger-700 border border-danger-200 rounded-2xl animate-shake flex items-center gap-3">
                    <X className="h-5 w-5" />
                    <strong>Error:</strong> {error}
                </div>
            )}

            {/* Confirmation Modal */}
            {showConfirmModal && (
                <Portal><div className="modal-overlay">
                    <div className="modal max-w-xl">
                        <div className="text-center space-y-4">
                            <div className="h-20 w-20 bg-warning-100 rounded-3xl flex items-center justify-center mx-auto mb-6">
                                <Rocket className="h-10 w-10 text-warning-600" />
                            </div>
                            <h2 className="text-4xl font-black text-slate-900 dark:text-white">Begin Deploy?</h2>
                            <p className="text-slate-500 font-medium">This will stop all verification workers, swap the source code, and perform a full system restart.</p>
                        </div>

                        <div className="grid grid-cols-2 gap-4 mt-8">
                            <button onClick={() => setShowConfirmModal(false)} className="btn btn-secondary py-4 text-lg">Cancel</button>
                            <button onClick={handleConfirmInstall} className="btn btn-primary py-4 text-lg">Deploy Now</button>
                        </div>
                    </div>
                </div></Portal>
            )}
        </div>
    );
}
